import React from 'react';
import { ArrowLeft } from 'lucide-react';
import MarketingHeader from '../../components/MarketingHeader';
import { REQUEST_ACCESS_URL } from '../../config/links';
import LegalFooter from '../../components/LegalFooter';
import useDocumentMeta from '../../hooks/useDocumentMeta';

const HardwareStartupsPage = () => {

  useDocumentMeta({
    title: 'Factory OS for Hardware Startups — The Engineering Operating System',
    description:
      "Engineering knowledge shouldn't disappear every time a product ships. Factory OS gives every decision, requirement, test, tradeoff, and lesson a permanent home inside the engineering workflow — so every revision starts smarter than the last. First 100 nodes free; $50/user/month under five engineers.",
    path: '/for/hardware-startups',
  });

  return (
    <div className="fos-notebook min-h-screen">
      <MarketingHeader />

      {/* ══════════ HERO ══════════ */}
      <section className="pt-14 sm:pt-24 pb-10 sm:pb-16 px-5">
        <div className="max-w-[1080px] mx-auto">
          <a href="/" className="inline-flex items-center gap-2 no-underline mb-8" style={{ fontFamily: 'var(--mono)', fontSize: 'var(--t-sm)', color: 'var(--ink-3)', letterSpacing: '0.4px' }}>
            <ArrowLeft size={14} /> Back to Factory OS
          </a>

          <div className="mb-5">
            <span className="nb-eyebrow" style={{ color: 'var(--nb-accent)' }}>
              the engineering operating system · for hardware startups
            </span>
          </div>

          <h1 style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 'var(--t-lg)', lineHeight: 1.05, color: 'var(--ink)', letterSpacing: '-0.5px' }}>
            Built for hardware startups.<br />
            <span className="nb-under">Built for shipping parts.</span>
          </h1>

          <p className="mt-5 max-w-[680px]" style={{ fontFamily: 'var(--hand)', fontSize: 'var(--t-md)', color: 'var(--ink-2)', lineHeight: 1.45 }}>
            Most hardware startups don't struggle because they lack software. They struggle because engineering knowledge disappears between revisions. Factory OS gives every decision, requirement, test, tradeoff, and lesson a permanent home inside the engineering workflow.
          </p>

          <p className="mt-3 max-w-[680px]" style={{ fontFamily: 'var(--script)', color: 'var(--nb-accent)', fontSize: 'var(--t-md)', lineHeight: 1.2 }}>
            Every revision starts smarter than the last.
          </p>

          <div className="flex flex-wrap gap-3 mt-7">
            <a href={REQUEST_ACCESS_URL} className="nb-btn no-underline text-center">Request access</a>
            <a href="https://demo.factory-os.com" className="nb-btn nb-btn-ghost">▶ Take the 2-minute tour</a>
          </div>

          <div className="mt-4" style={{ fontFamily: 'var(--mono)', fontSize: 'var(--t-sm)', color: 'var(--ink-3)', letterSpacing: '0.4px' }}>
            your first 100 nodes are free · then $50/user/mo under five engineers
          </div>
        </div>
      </section>

      {/* ══════════ THE TRAP ══════════ */}
      <section className="py-12 md:py-[72px] px-5" style={{ borderTop: '1px dashed var(--ink-4)' }}>
        <div className="max-w-[1080px] mx-auto">
          <div className="max-w-[720px] mb-7">
            <div className="nb-eyebrow">the trap</div>
            <h2 className="mt-2" style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 'var(--t-lg)', lineHeight: 1.1, color: 'var(--ink)' }}>
              Engineering knowledge disappears.
            </h2>
            <p className="mt-3" style={{ fontFamily: 'var(--hand)', fontSize: 'var(--t-md)', color: 'var(--ink-2)' }}>
              Not because engineers aren't documenting enough. Because today's tools weren't built to preserve engineering reasoning.
            </p>
          </div>

          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 list-none p-0 m-0">
            {[
              "Decisions disappear into Slack, meetings, and notebooks.",
              "Test results become PDFs no one references.",
              "Requirements lose the evidence behind them.",
              "New engineers spend weeks rediscovering why things are the way they are.",
              "Every program relearns lessons the last one already paid for.",
            ].map((item, i) => (
              <li key={i} className="nb-sketch-thin p-4" style={{ fontFamily: 'var(--hand)', fontSize: 'var(--t-md)', color: 'var(--ink-2)', lineHeight: 1.4 }}>
                <span style={{ fontFamily: 'var(--mono)', color: 'var(--ink-4)', marginRight: 8 }}>✗</span>{item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ══════════ HOW IT FITS ══════════ */}
      <section className="py-12 md:py-[72px] px-5" style={{ borderTop: '1px dashed var(--ink-4)' }}>
        <div className="max-w-[1080px] mx-auto">
          <div className="max-w-[720px] mb-7">
            <div className="nb-eyebrow" style={{ color: 'var(--nb-accent)' }}>what changes</div>
            <h2 className="mt-2" style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 'var(--t-lg)', lineHeight: 1.1, color: 'var(--ink)' }}>
              What your team gets back.
            </h2>
          </div>

          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 list-none p-0 m-0">
            {[
              ['Stop repeating mistakes', 'The next revision opens with the last failure already cited. Lessons carry forward automatically — no archaeology.'],
              ['Onboard engineers in days, not weeks', 'The why is on the node. New engineers read the record instead of interviewing the whole company.'],
              ['Pass design reviews with evidence you already have', 'Every requirement carries its verification method and test results. The review package is a byproduct of the work, not a weekend.'],
              ['Remember why every decision was made', "Every concept you didn't pick stays on the node — with the reason, the cost delta, and the lead-time delta."],
              ['Move faster on every revision', 'The engineering cycle keeps evidence attached as you work, so momentum comes from a proven base instead of rework.'],
              ['Start small, scale without switching', 'Your first 100 nodes are free. Teams under five engineers pay $50/user/month for the full Team tier. Start with your first product; scale to your entire engineering organization.'],
            ].map(([title, body], i) => (
              <li key={i} className="nb-sketch-thin p-4">
                <div style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 'var(--t-md)', color: 'var(--ink)', marginBottom: 6 }}>{title}</div>
                <div style={{ fontFamily: 'var(--hand)', fontSize: 'var(--t-md)', color: 'var(--ink-2)', lineHeight: 1.4 }}>{body}</div>
              </li>
            ))}
          </ul>

          <div className="nb-sticky mt-7" style={{ transform: 'rotate(-0.4deg)' }}>
            <b>⚡ "Why did we pick 4130 over Ti?"</b><br />
            That answer used to live in the last engineer's Slack. Now it's on the node — with the cost delta and the lead-time tradeoff. Diligence reads it like a designed audit trail.
          </div>
        </div>
      </section>

      {/* ══════════ CLOSER ══════════ */}
      <section className="py-12 md:py-[72px] px-5 text-center" style={{ borderTop: '1px dashed var(--ink-4)' }}>
        <div className="max-w-[820px] mx-auto">
          <h2 style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 'var(--t-lg)', lineHeight: 1.1, color: 'var(--ink)', marginBottom: 14 }}>
            Ship the next revision from a proven base.
          </h2>
          <p className="mb-7" style={{ fontFamily: 'var(--script)', fontSize: 'var(--t-md)', color: 'var(--nb-accent)' }}>
            Not from memory. Not from a slack channel. From the record.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <a href={REQUEST_ACCESS_URL} className="nb-btn no-underline text-center">Request access</a>
            <a href="https://demo.factory-os.com" className="nb-btn nb-btn-ghost">▶ Take the 2-minute tour</a>
          </div>
        </div>
      </section>

      <LegalFooter />
    </div>
  );
};

export default HardwareStartupsPage;
